/**
 * Type definitions for the Car Search Results Page (SRP)
 */

// Car-related types
export interface Car {
  vin: string;
  make: string;
  model: string;
  year: number;
  trim?: string;
  price: number;
  odometer: number;
  photoUrls: string[];
  numberOfPhotos: number;
  status?: 'active' | 'hidden' | 'deposit' | 'sold' | 'cancelled' | 'coming soon' | 'sales pending';
  exportId?: string;
  createdAt?: string | number;
  updatedAt?: number;
  images?: string[]; // Alternative property name for photoUrls
  document?: Car; // Some responses wrap the car in a document property
  
  // Vehicle specifications
  transmission?: string;
  drivetrain?: string;
  fuel?: string; // Fuel type
  doors?: number;
  extColor?: string; // "Black Metallic"
  extColorGeneric?: string; // "Black"
  interiorColor?: string; // "Gray"
  interiorMaterial?: string; // "Cloth front seat upholstery"
  bodyStyle?: string; // "Regular Cab"
  bodyType?: string; // "Truck"
  
  // Admin/Inventory specific fields (optional for public-facing car data)
  dealerId?: string;
  dealerName?: string;
  dealerStockId?: string;
  stockId?: string;
  dealerPrice?: number;
  processedPhotos?: string[];
  photosUpdatedAt?: string;
  vehicleStyle?: string;
  modelCode?: string;
  chromeStyleId?: string;
  postalCode?: string;
  deleteTime?: number;
  
  // Engine specifications
  engine?: string;
  engineDisplacement?: string;
  cityMPG?: number;
  hwyMPG?: number;
  
  // Standard options and features (flexible type for different implementations)
  standardOptions?: string[] | string | any[];
  yearString?: string;
  
  // Dealer comments and notes
  comments?: string;

  // Highest-priority inventory-segment badge (computed server-side, never stored)
  segmentBadge?: import('./segments').SegmentBadge | null;
}

// Static tile type for promotional content
export interface StaticTileData {
  textBeforeHighlighted?: string;
  highlightedText?: string;
  textAfterHighlighted?: string;
  contactNumber?: string;
  tileBackgroundColor?: string;
  hightlightedTextColor?: string;
  isClickable?: boolean;
  tileLink?: string;
  tileType?: 'support' | 'trade_appraisal' | 'sell_my_car' | 'financing';
}

export interface StaticTile {
  backgroundColor?: string;
}

// Combined car item that can be either a car or a static tile
export type CarListItem = {
  document: Car | StaticTileData;
};

export type CarDocument = CarListItem;

// Facet types for filtering
export interface Facet {
  value: string;
  count: number;
  label?: string; // Some facets include a label
}

export interface FacetGroup {
  make: Facet[];
  bodyStyle: string[];
  transmission: string[];
  fuelType: string[];
  driveType: string[];
  cylinders: string[];
  engines: string[];
  doors: number[];
  exColor: string[];
  inColor: string[];
  price: [number, number];
  year: [number, number];
  mileage: [number, number];
}

// Filter state type
export interface FilterState {
  collection?: string;
  make?: string[];
  model?: string[];
  bodystyle?: string[];
  year?: [number, number];
  price?: [number, number];
  mileage?: [number, number];
  transmission?: string[];
  fuel?: string[];
  train?: string[]; // Drive train
  cylinder?: string[];
  engine?: string[];
  doors?: number[];
  excolor?: string[]; // Exterior color
  incolor?: string[]; // Interior color
  q?: string; // Search query
  sort?: SortOption;
  page?: number;
  limit?: number;
  shopOnline?: boolean;
}

// Sort options
export type SortOption = 'newest' | 'priceLow' | 'priceHigh' | 'milesLow' | 'newModel' | 'specials';

// Sort configuration
export interface SortConfig {
  value: SortOption;
  label: string;
  orderBy: string;
  orderAsc: boolean;
}

// URL search params (from Next.js)
export interface SearchParams {
  [key: string]: string | string[] | undefined;
}

// Pagination
export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// API Response types
export interface CarsResponse {
  items: CarListItem[];
  pagination: Pagination;
  hits?: CarListItem[]; // Alternative property name for items
}

export interface CarListingResponse extends CarsResponse {}

export interface FilterListingResponse extends CarsResponse {}
export interface SearchCarsResponse extends CarsResponse {}
export interface GetAllCarsResponse extends CarsResponse {}

// Sanity CMS types
export interface ContactDetails {
  contactList: StaticTileData[];
}

export interface CarTilesFooter {
  footerText?: string;
  footerTextTC?: string;
}

export interface FindYourRideData {
  contactDetails: ContactDetails;
  carTilesFooter: CarTilesFooter;
  metaData?: {
    title: string;
    description: string;
  };
}

// Model type for make/model relationships
export interface Model {
  value: string;
  label: string;
  make: string;
}

// Props types for components
export interface SearchSideProps {
  facets: FacetGroup;
  initialFilters: FilterState;
}

export interface SearchMenuProps {
  initialCars: CarsResponse;
  filters: FilterState;
  carTilesFooter: CarTilesFooter;
}

export interface SearchNavProps {
  initialQuery?: string;
}

export interface FilterBubblesProps {
  filters: FilterState;
}

// Utility types
export type RangeValue = [number, number];

export interface SelectOption {
  value: string;
  label: string;
  color?: string;
}

// API query parameters
export interface CarQuery {
  page?: number;
  perPage?: number;
  orderBy?: string;
  orderAsc?: boolean;
  q?: string;
}

export interface FilterQuery extends CarQuery {
  make?: string | string[];
  model?: string | string[];
  bodystyle?: string | string[];
  doors?: string | string[];
  cylinder?: string | string[];
  fuel?: string | string[];
  engine?: string | string[];
  train?: string | string[];
  transmission?: string | string[];
  incolor?: string | string[];
  excolor?: string | string[];
  price?: string | string[];
  year?: string | string[];
  mileage?: string | string[];
  shopOnline?: string | boolean;
}

// Service function signatures
export type GetAllCarsService = (
  query: CarQuery, 
  shopOnline?: boolean, 
  ipAddress?: string
) => Promise<GetAllCarsResponse>;

export type FilterListingService = (
  queryString: string, 
  perPage: number, 
  shopOnline?: boolean, 
  ipAddress?: string
) => Promise<FilterListingResponse>;

export type SearchCarsService = (
  query: CarQuery, 
  shopOnline?: boolean, 
  ipAddress?: string
) => Promise<SearchCarsResponse>;

export type GetAllFacetsService = (
  showComingSoon?: boolean, 
  shopOnline?: boolean, 
  ipAddress?: string
) => Promise<FacetGroup>;

export type GetMakesService = () => Promise<Facet[]>;
export type GetModelsService = () => Promise<Model[]>;
export type FindYourRidePageContentService = () => Promise<FindYourRideData>;

// Constants
export const DEFAULT_PER_PAGE = 27;
export const DEFAULT_SORT: SortOption = 'newest';
export const CONTACT_TILE_INTERVAL = 5; // Show a marketing tile in every fifth displayed slot
